import React from 'react';

import {
  TextCustom,
  TextAppearance,
  Breakpoints,
  TextSize,
  TextWeight,
  TextColor,
  TextLineHeight,
  TextTag,
} from '@vw-marketing/us-components';

import { applyTextTransformations } from '../../utils/applyTextTransformations';
import {
  StyledSubscriptionCardInformationWrapper,
  StyledSubscriptionCardWrapper,
} from './styled';

interface SubscriptionCardProps {
  id: string;
  planName: string;
  providerName: string;
  isCanada?: boolean;
  translationHandler: (key: string) => string;
}

interface ProviderInformation {
  providerName: string;
  description: string;
}

export const SubscriptionCard = ({ id, planName, providerName, isCanada = false, translationHandler }: SubscriptionCardProps) => {

  const getProviderInformation = (
    planName: string, providerName: string
  ): ProviderInformation | undefined => {
    const lowerPlanName = planName.toLowerCase();
    const lowerProviderName = providerName.toLowerCase();

    if (isCanada) {
      if (lowerProviderName.includes('verizon') ||
          lowerProviderName.includes('sirius')) {
        return undefined;
      }
    }

    const providerMatchers = [
      {
        name: 'tMobile',
        patterns: ['tmobile', 't-mobile', 't mobile', 'tmo'],
        translationKey: isCanada ? 'Rogers' : 'tMobile'
      },
      {
        name: 'Verizon',
        patterns: ['verizon'],
        translationKey: 'Verizon'
      },
      {
        name: 'SiriusXM',
        patterns: ['siriusxm', 'sirius-xm', 'sirius xm', 'sirius'],
        translationKey: 'SiriusXM'
      },
    ];

    const matchedProvider = providerMatchers.find(provider =>
      provider.patterns.some(pattern =>
        lowerPlanName.includes(pattern) || lowerProviderName.includes(pattern)
      )
    );

    if (matchedProvider) {
      return {
        providerName: translationHandler(
          `accountSettings.deactivate.myvwaccount.subscriptions.${matchedProvider.translationKey}.label`,
        ),
        description: translationHandler(
          `accountSettings.deactivate.myvwaccount.subscriptions.${matchedProvider.translationKey}.description`,
        ),
      };
    }

    return undefined;
  };

  const providerInformation = getProviderInformation(planName, providerName);

  if (!providerInformation) return <></>

  return (
    <StyledSubscriptionCardWrapper key={id}>
      <StyledSubscriptionCardInformationWrapper>
        <TextCustom
          tag={TextTag.span}
          appearance={TextAppearance.copy200}
          style={{
            [Breakpoints.default]: {
              fontSize: TextSize.TextSize16,
              fontWeight: TextWeight.regular,
              color: TextColor.primary,
              lineHeight: TextLineHeight.TextLineHeight24,
            },
            [Breakpoints.b1920]: {
              fontSize: TextSize.TextSize20,
              fontWeight: TextWeight.regular,
              color: TextColor.primary,
              lineHeight: TextLineHeight.TextLineHeight32,
            },
          }}
        >
          {applyTextTransformations(providerInformation?.providerName || '', [
            'T-mobile',
            'SiriusXM',
            'Verizon',
          ]) || ''}
        </TextCustom>
        <TextCustom
          tag={TextTag.span}
          appearance={TextAppearance.copy100}
          style={{
            [Breakpoints.default]: {
              fontSize: TextSize.TextSize14,
              fontWeight: TextWeight.regular,
              color: TextColor.secondary,
              lineHeight: TextLineHeight.TextLineHeight24,
            },
            [Breakpoints.b1920]: {
              fontSize: TextSize.TextSize16,
              fontWeight: TextWeight.regular,
              color: TextColor.secondary,
              lineHeight: TextLineHeight.TextLineHeight24,
            },
          }}
        >
          {applyTextTransformations(providerInformation?.description || '', [
            'T-mobile',
            'SiriusXM',
            'Verizon',
          ])}
        </TextCustom>
      </StyledSubscriptionCardInformationWrapper>
    </StyledSubscriptionCardWrapper>
  );
};
